import { datadogRum } from "@datadog/browser-rum";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { usePlannedMenuHeaderQuery } from "apps/web/src/types";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import DiscardChangesDialog from "../../../components/universal/DiscardChangesDialog";
import LoadingBackdrop from "../../../components/universal/LoadingBackdrop";
import { PlannedMenuForm } from "./PlannedMenuForm";
import {
  PlannedMenuFormSchema,
  PlannedMenuFormType,
  plannedMenuFormDefaultValues,
  plannedMenuFormToEditInput,
} from "./PlannedMenuFormSchema";
import { usePlannedMenuPlannedMenuMutation } from "./PlannedMenusFormProvider";

interface PlannedMenuEditDialogProps {
  open: boolean;
  onClose: () => void;
  plannedMenuId: string;
}

export const PlannedMenuEditDialog = ({ open, onClose, plannedMenuId }: PlannedMenuEditDialogProps) => {
  const [discardChangesOpen, setDiscardChangesOpen] = useState(false);
  const { editPlannedMenu, editPlannedMenuCalled } = usePlannedMenuPlannedMenuMutation();
  const form = useForm<PlannedMenuFormType>({
    defaultValues: plannedMenuFormDefaultValues(),
    resolver: zodResolver(PlannedMenuFormSchema),
    mode: "onChange",
  });
  const { loading: loadingOriginalMenu } = usePlannedMenuHeaderQuery({
    variables: { plannedMenuId },
    onCompleted: data => {
      // since both of these are hooks, have to reset the form after this completes
      form.reset(plannedMenuFormDefaultValues(data.plannedMenu));
    },
    fetchPolicy: "network-only",
  });

  const { isDirty, isValid } = form.formState;

  const handleClose = () => {
    if (isDirty) {
      setDiscardChangesOpen(true);
    } else {
      onClose();
    }
  };

  const submit = async (newMenu: PlannedMenuFormType) => {
    datadogRum.addAction("menu_builder.edited_menu", { menuId: plannedMenuId });
    editPlannedMenu({ variables: { input: plannedMenuFormToEditInput(newMenu, plannedMenuId) } });
  };

  return loadingOriginalMenu || editPlannedMenuCalled ? (
    <LoadingBackdrop open={open} onClose={onClose} />
  ) : (
    <Dialog
      PaperProps={{
        sx: { width: "900px", height: "600px" },
      }}
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle title="Edit Menu" onClose={handleClose} />
      <DialogContent>
        <PlannedMenuForm form={form} mode="edit" />
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!isValid} onClick={form.handleSubmit(submit)}>
            Save
          </Button>
        </Box>
      </DialogActions>
      {discardChangesOpen && (
        <DiscardChangesDialog
          open={discardChangesOpen}
          onClose={() => setDiscardChangesOpen(false)}
          onDiscard={onClose} />
      )}
    </Dialog>
  );
};
